import React, { useEffect, useState } from 'react';
import BackButton from "../components/BackButton";
import FishingRulesService from '../services/FishingRules';

var TYPE_COLORS = {
  river: '#0d6efd', creek: '#20c997', lake: '#198754', reservoir: '#6f42c1',
  pond: '#6c757d', swa: '#fd7e14', state_park: '#dc3545', national_forest: '#198754', other: '#6c757d',
};
var TYPE_LABELS = {
  river: 'River', creek: 'Creek', lake: 'Lake', reservoir: 'Reservoir',
  pond: 'Pond', swa: 'SWA', state_park: 'State Park', national_forest: 'Nat. Forest', other: 'Other',
};

function TypeBadge({ type }) {
  if (!type) return <span style={{ color: '#999' }}>{'\u2014'}</span>;
  var color = TYPE_COLORS[type] || '#6c757d';
  var label = TYPE_LABELS[type] || type;
  return (
    <span style={{ backgroundColor: color, color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 'bold' }}>{label}</span>
  );
}

function ConfidenceBar({ score }) {
  var color = score >= 70 ? '#198754' : score >= 40 ? '#ffc107' : '#dc3545';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div style={{ width: '50px', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ width: score + '%', height: '100%', backgroundColor: color, borderRadius: '4px' }} />
      </div>
      <span style={{ fontSize: '0.7em', color: '#666' }}>{score}</span>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{ backgroundColor: color || '#f8f9fa', borderRadius: '8px', padding: '12px 16px', textAlign: 'center', minWidth: '110px' }}>
      <div style={{ fontSize: '1.8em', fontWeight: 'bold' }}>{value}</div>
      <div style={{ fontSize: '0.8em', color: '#666' }}>{label}</div>
    </div>
  );
}

function formatBagLimit(bag) {
  if (!bag || bag.length === 0) return '\u2014';
  return bag.map(function(b) { return b.limit + ' ' + (b.species || 'fish'); }).join(', ');
}

var CELL = { wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', verticalAlign: 'middle', fontSize: '0.85em' };

var FishingRules = function() {
  var _rules = useState([]);
  var rules = _rules[0], setRules = _rules[1];
  var _stats = useState({});
  var stats = _stats[0], setStats = _stats[1];
  var _filters = useState({ search: '', region: '', drainage: '', water_body_type: '', needs_review: '' });
  var filters = _filters[0], setFilters = _filters[1];
  var _filterOptions = useState({ regions: [], drainages: [], types: [] });
  var filterOptions = _filterOptions[0], setFilterOptions = _filterOptions[1];
  var _sortField = useState('water_body_name');
  var sortField = _sortField[0], setSortField = _sortField[1];
  var _sortDir = useState('asc');
  var sortDir = _sortDir[0], setSortDir = _sortDir[1];
  var _loading = useState(true);
  var loading = _loading[0], setLoading = _loading[1];
  var _parsing = useState(false);
  var parsing = _parsing[0], setParsing = _parsing[1];
  var _selected = useState(null);
  var selected = _selected[0], setSelected = _selected[1];
  var _detailLoading = useState(false);
  var detailLoading = _detailLoading[0], setDetailLoading = _detailLoading[1];

  function loadRules() {
    setLoading(true);
    var f = Object.assign({}, filters, { sort: sortField, sort_dir: sortDir });
    FishingRulesService.getRules(f).then(function(resp) {
      setRules(resp.rules || []);
      setStats({ total: resp.total_count, filtered: resp.filtered_count, review: resp.needs_review_count, high: resp.high_confidence_count });
      setFilterOptions({ regions: resp.regions || [], drainages: resp.drainages || [], types: resp.types || [] });
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }

  function loadDetail(id) {
    setDetailLoading(true);
    FishingRulesService.getRule(id).then(function(resp) {
      setSelected(resp.rule);
      setDetailLoading(false);
    }).catch(function() { setDetailLoading(false); });
  }

  function handleParse() {
    setParsing(true);
    FishingRulesService.triggerParse().then(function(resp) {
      setParsing(false);
      alert(resp.message || 'Parsing started');
    }).catch(function() { setParsing(false); });
  }

  function handleSort(field) {
    if (sortField === field) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  }

  useEffect(function() { loadRules(); }, [filters, sortField, sortDir]);

  // Detail view
  if (selected) {
    var r = selected;
    return (
      <div className="m-4">
        <button className="btn btn-sm btn-outline-secondary mb-3" onClick={function() { setSelected(null); loadRules(); }}>&larr; Back to Rules</button>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h3 className="mb-1">{r.water_body_name}</h3>
            <span className="text-muted">{r.wb_rule_id} &middot; <TypeBadge type={r.water_body_type} /> &middot; {r.drainage || 'Unknown drainage'}</span>
          </div>
          <ConfidenceBar score={r.parse_confidence || 0} />
        </div>
        <div className="row mb-4">
          <div className="col-md-6">
            <h5>Regulations</h5>
            <table className="table table-sm"><tbody>
              <tr><td style={{width:'40%'}}><strong>Bag Limit</strong></td><td>{formatBagLimit(r.bag_limit)}</td></tr>
              <tr><td><strong>Possession</strong></td><td>{formatBagLimit(r.possession_limit)}</td></tr>
              <tr><td><strong>Size Restriction</strong></td><td>{r.size_restriction ? JSON.stringify(r.size_restriction) : '\u2014'}</td></tr>
              <tr><td><strong>Gear</strong></td><td>{r.gear_restriction || '\u2014'}</td></tr>
              <tr><td><strong>Method</strong></td><td>{r.method_restriction || '\u2014'}</td></tr>
              <tr><td><strong>Season</strong></td><td>{r.season_dates || '\u2014'}</td></tr>
              <tr><td><strong>Seasonal Closures</strong></td><td>{r.seasonal_closures ? JSON.stringify(r.seasonal_closures) : '\u2014'}</td></tr>
              <tr><td><strong>Species</strong></td><td>{(r.species_present || []).join(', ') || '\u2014'}</td></tr>
            </tbody></table>
          </div>
          <div className="col-md-6">
            <h5>Access & Location</h5>
            <table className="table table-sm"><tbody>
              <tr><td style={{width:'40%'}}><strong>Access Type</strong></td><td>{r.access_type || '\u2014'}</td></tr>
              <tr><td><strong>Location</strong></td><td>{r.location_description || '\u2014'}</td></tr>
              <tr><td><strong>Region</strong></td><td>{r.region || '\u2014'}</td></tr>
              <tr><td><strong>Drainage</strong></td><td>{r.drainage || '\u2014'}</td></tr>
              <tr><td><strong>Needs Review</strong></td><td>{r.needs_review ? 'Yes' : 'No'}</td></tr>
              <tr><td><strong>Notes</strong></td><td>{r.notes || '\u2014'}</td></tr>
            </tbody></table>
          </div>
        </div>
        {r.special_regulations && (
          <div className="mb-4">
            <h5>Special Regulations</h5>
            <p style={{ fontSize: '0.9em', color: '#444', whiteSpace: 'pre-wrap' }}>{r.special_regulations}</p>
          </div>
        )}
        {r.raw_text && (
          <div className="mb-4">
            <h5>Raw OCR Text</h5>
            <pre style={{ fontSize: '0.8em', backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px', maxHeight: '300px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{r.raw_text}</pre>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="m-4">
      <BackButton />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Fishing Rules</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-primary" disabled={parsing} onClick={handleParse}>
            {parsing ? 'Parsing...' : 'Parse OCR'}
          </button>
        </div>
      </div>

      <div className="d-flex gap-3 mb-3 flex-wrap">
        <SummaryCard label="Total Rules" value={stats.total || 0} />
        <SummaryCard label="Needs Review" value={stats.review || 0} color={stats.review > 0 ? '#fff3cd' : '#f8f9fa'} />
        <SummaryCard label="High Confidence" value={stats.high || 0} color="#d1e7dd" />
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <input type="text" className="form-control form-control-sm" placeholder="Search water bodies..."
            value={filters.search} onChange={function(e) { setFilters(Object.assign({}, filters, { search: e.target.value })); }} />
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filters.drainage}
            onChange={function(e) { setFilters(Object.assign({}, filters, { drainage: e.target.value })); }}>
            <option value="">All Drainages</option>
            {filterOptions.drainages.map(function(d) { return <option key={d} value={d}>{d}</option>; })}
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filters.water_body_type}
            onChange={function(e) { setFilters(Object.assign({}, filters, { water_body_type: e.target.value })); }}>
            <option value="">All Types</option>
            {filterOptions.types.map(function(t) { return <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>; })}
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filters.needs_review}
            onChange={function(e) { setFilters(Object.assign({}, filters, { needs_review: e.target.value })); }}>
            <option value="">All</option>
            <option value="true">Needs Review</option>
            <option value="false">Reviewed</option>
          </select>
        </div>
      </div>

      <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '8px' }}>
        Showing {rules.length} of {stats.filtered || 0} rules (filtered from {stats.total || 0} total)
      </div>

      {loading ? (
        <div className="text-center p-4"><em>Loading rules...</em></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-sm table-hover table-striped" style={{ fontSize: '0.82em' }}>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={function() { handleSort('water_body_name'); }}>
                  Water Body {sortField === 'water_body_name' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th>Type</th>
                <th style={{ cursor: 'pointer' }} onClick={function() { handleSort('drainage'); }}>
                  Drainage {sortField === 'drainage' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th>Bag Limit</th>
                <th>Gear Restriction</th>
                <th>Season</th>
                <th>Species</th>
                <th>Access</th>
                <th style={{ cursor: 'pointer' }} onClick={function() { handleSort('parse_confidence'); }}>
                  Confidence {sortField === 'parse_confidence' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th>Review</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(function(r) {
                return (
                  <tr key={r.id}>
                    <td style={Object.assign({}, CELL, { fontWeight: 'bold', cursor: 'pointer', color: '#0d6efd', maxWidth: '200px' })}
                      onClick={function() { loadDetail(r.id); }}>
                      {r.water_body_name}
                      <div style={{ fontSize: '0.75em', color: '#999', fontWeight: 'normal' }}>{r.wb_rule_id}</div>
                    </td>
                    <td style={CELL}><TypeBadge type={r.water_body_type} /></td>
                    <td style={CELL}>{r.drainage || '\u2014'}</td>
                    <td style={CELL}>{formatBagLimit(r.bag_limit)}</td>
                    <td style={Object.assign({}, CELL, { maxWidth: '150px', fontSize: '0.78em' })}>{r.gear_restriction || '\u2014'}</td>
                    <td style={CELL}>{r.season_dates || '\u2014'}</td>
                    <td style={Object.assign({}, CELL, { fontSize: '0.75em', maxWidth: '120px' })}>{(r.species_present || []).slice(0, 3).join(', ') || '\u2014'}</td>
                    <td style={CELL}>{r.access_type || '\u2014'}</td>
                    <td style={CELL}><ConfidenceBar score={r.parse_confidence || 0} /></td>
                    <td style={CELL}>
                      {r.needs_review ? <span style={{ backgroundColor: '#ffc107', color: '#000', padding: '1px 6px', borderRadius: '8px', fontSize: '0.7em' }}>Review</span> : <span style={{ color: '#198754', fontSize: '0.75em' }}>OK</span>}
                    </td>
                    <td style={CELL}>
                      <button className="btn btn-sm btn-outline-primary" style={{ fontSize: '0.75em', padding: '1px 6px' }}
                        onClick={function() { loadDetail(r.id); }}>Detail</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FishingRules;
